import React, { Dispatch, SetStateAction } from 'react';

import {
  BasicBudget,
  Budget,
  BudgetCategory,
  BudgetItem,
  BudgetItemTemplate,
  BudgetsConnectionInput,
  BudgetSummary,
  BudgetSummaryConnectionInput,
  BudgetType,
  BudgetUnit,
} from '@atom/types/budget';

import { FetchParentProps } from './BudgetDetail';
import { EditField } from './budgetDetailUtils';

interface Context {
  budget: Budget;
  parentBudgetUnit: BudgetUnit;
  setParentBudgetUnit: Dispatch<SetStateAction<BudgetUnit>>;
  childBudgetUnits: BudgetUnit[];
  setChildBudgetUnits: Dispatch<SetStateAction<BudgetUnit[]>>;
  budgetCategories: BudgetCategory[];
  setBudgetCategories: Dispatch<SetStateAction<BudgetCategory[]>>;
  categoryFilters: BudgetCategory[];
  setCategoryFilters: Dispatch<SetStateAction<BudgetCategory[]>>;
  categoryIds: string[];
  budgetTypes: string[];
  setTypeFilters: Dispatch<SetStateAction<BudgetType[]>>;
  typeFilters: BudgetType[];
  budgetItemTemplateFilters: BudgetItemTemplate[];
  setBudgetItemTemplateFilters: Dispatch<SetStateAction<BudgetItemTemplate[]>>;
  budgetItemTemplateNames: string[];
  editingItem: BudgetItem;
  setEditingItem: Dispatch<SetStateAction<BudgetItem>>;
  editingField: EditField;
  setEditingField: Dispatch<SetStateAction<EditField>>;
  expandedCategories: Set<string>;
  setExpandedCategories: Dispatch<SetStateAction<Set<string>>>;
  excludeZeroBudgetItems: boolean;
  setExcludeZeroBudgetItems: Dispatch<SetStateAction<boolean>>;
  fetchParentUnit: (props: FetchParentProps) => void;
  loadingParentUnit: boolean;
  comparisonBudgets: BasicBudget[];
  setComparisonBudgets: Dispatch<SetStateAction<BasicBudget[]>>;
  budgetSummaries: BudgetSummary[];
  setBudgetSummaries: Dispatch<SetStateAction<BudgetSummary[]>>;
  budgetOverviewSummaries: BudgetSummary[];
  setBudgetOverviewSummaries: Dispatch<SetStateAction<BudgetSummary[]>>;
  openComparisonRowId: string;
  setOpenComparisonRowId: Dispatch<SetStateAction<string>>;
  fetchSummaries: (options: {
    variables: { input: BudgetSummaryConnectionInput };
  }) => void;
  loadingSummaries: boolean;
  itemHoverId: string;
  setItemHoverId: Dispatch<SetStateAction<string>>;
  showTracking: boolean;
  setShowTracking: Dispatch<SetStateAction<boolean>>;
  showExpenditures: boolean;
  showBudgetTypes: boolean;
  showApprovalFlow: boolean;
  showAuthorizedBudget: boolean;
  setShowChart: Dispatch<SetStateAction<boolean>>;
  showChart: boolean;
  loadingBudgetOptions: boolean;
  totalBudgetOptions: number;
  setTotalBudgetOptions: Dispatch<SetStateAction<number>>;
  budgetOptions: Budget[];
  getBudgetOptions: (options: {
    variables: { input: BudgetsConnectionInput };
  }) => void;
  selectedUnits: Set<string>;
  setSelectedUnits: Dispatch<SetStateAction<Set<string>>>;
}

export const initialState: Context = {
  budget: null,
  parentBudgetUnit: null,
  setParentBudgetUnit: () => {},
  childBudgetUnits: null,
  setChildBudgetUnits: () => {},
  budgetCategories: null,
  setBudgetCategories: () => {},
  categoryFilters: [],
  setCategoryFilters: () => {},
  categoryIds: [],
  budgetTypes: [],
  setTypeFilters: () => {},
  typeFilters: [],
  budgetItemTemplateFilters: [],
  setBudgetItemTemplateFilters: () => {},
  budgetItemTemplateNames: [],
  editingItem: null,
  setEditingItem: () => {},
  editingField: null,
  setEditingField: () => {},
  expandedCategories: new Set([]),
  setExpandedCategories: () => {},
  excludeZeroBudgetItems: false,
  setExcludeZeroBudgetItems: () => {},
  fetchParentUnit: () => {},
  loadingParentUnit: null,
  comparisonBudgets: null,
  setComparisonBudgets: () => {},
  budgetSummaries: null,
  setBudgetSummaries: () => {},
  budgetOverviewSummaries: null,
  setBudgetOverviewSummaries: () => {},
  openComparisonRowId: null,
  setOpenComparisonRowId: () => {},
  fetchSummaries: () => {},
  loadingSummaries: null,
  itemHoverId: null,
  setItemHoverId: () => {},
  showTracking: null,
  setShowTracking: () => {},
  showExpenditures: null,
  showBudgetTypes: null,
  showApprovalFlow: null,
  showAuthorizedBudget: null,
  setShowChart: () => {},
  showChart: null,
  loadingBudgetOptions: null,
  totalBudgetOptions: null,
  setTotalBudgetOptions: () => {},
  budgetOptions: null,
  getBudgetOptions: () => {},
  selectedUnits: new Set([]),
  setSelectedUnits: () => {},
};

const BudgetDetailContext = React.createContext<Context>(initialState);

export default BudgetDetailContext;
